import React from 'react';
import { Card, BadgeDelta } from '@tremor/react';
import { Skeleton } from '@chakra-ui/react';
import { BarChart, Bar, Tooltip, Cell, ResponsiveContainer } from 'recharts';

// Valores chegam em centavos inteiros do backend: aqui so formata.
const brl = (centavos) => (Number(centavos || 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const brlCurto = (centavos) => {
  const reais = Number(centavos || 0) / 100;
  if (reais < 1000) return brl(centavos);
  return `R$ ${(reais / 1000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} mil`;
};

const iniciais = (nome = '') => nome.trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join('').toUpperCase();

// '2026-10-23' -> '23/10' sem passar por Date (evita deslocar o dia pelo fuso)
const diaMes = (dia = '') => dia.split('-').reverse().slice(0, 2).join('/');

const PERIODOS = [
  { key: 'hoje', titulo: 'Hoje', curto: 'Hoje', comparacao: 'vs ontem' },
  { key: 'd7', titulo: 'Últimos 7 dias', curto: '7 dias', comparacao: 'vs 7 dias antes' },
  { key: 'd30', titulo: 'Últimos 30 dias', curto: '30 dias', comparacao: 'vs 30 dias antes' },
];

const Variacao = ({ valor, rotulo }) => {
  if (valor == null) return <span className="text-[10px] text-slate-400">sem base {rotulo}</span>;
  const tipo = valor > 0 ? 'increase' : valor < 0 ? 'decrease' : 'unchanged';
  return (
    <span className="inline-flex items-center gap-1">
      <BadgeDelta deltaType={tipo} size="xs">{Math.abs(valor).toLocaleString('pt-BR')}%</BadgeDelta>
      {rotulo && <span className="text-[10px] text-slate-400">{rotulo}</span>}
    </span>
  );
};

const DicaDia = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-white border border-slate-200 shadow-lg rounded-lg px-2 py-1">
      <p className="text-[11px] font-bold text-slate-700">{diaMes(p.dia)}</p>
      <p className="text-[11px] text-orange-600 font-semibold">{brl(p.centavos)}</p>
    </div>
  );
};

const VendidoPorDia = ({ serie = [], largura }) => {
  const grafico = (
    <BarChart data={serie} width={largura} height={36} margin={{ top: 2, right: 0, left: 0, bottom: 0 }} barCategoryGap={1}>
      <Tooltip content={<DicaDia />} cursor={{ fill: '#fff7ed' }} />
      <Bar dataKey="centavos" minPointSize={2} radius={[2, 2, 0, 0]} isAnimationActive={false}>
        {serie.map((p) => <Cell key={p.dia} fill={p.centavos > 0 ? '#f97316' : '#e2e8f0'} />)}
      </Bar>
    </BarChart>
  );
  if (largura) return grafico;
  return <ResponsiveContainer width="100%" height={36}>{grafico}</ResponsiveContainer>;
};

const Avatar = ({ nome }) => (
  <div className="h-9 w-9 shrink-0 rounded-full bg-orange-100 text-orange-700 font-bold text-xs flex items-center justify-center ring-2 ring-white shadow-sm">
    {iniciais(nome)}
  </div>
);

const Participacao = ({ pct }) => (
  <>
    <div className="mt-2 h-1.5 w-24 rounded-full bg-slate-100">
      <div className="h-1.5 rounded-full bg-orange-500" style={{ width: `${Math.min(Number(pct || 0), 100)}%` }} />
    </div>
    <div className="text-[11px] text-slate-500 mt-1">{pct == null ? '--' : `${Number(pct).toLocaleString('pt-BR')}% do faturamento`}</div>
  </>
);

const LinhaVendedor = ({ v }) => (
  <tr className="border-t border-slate-100 hover:bg-orange-50/40 transition-colors">
    <td className="px-3 py-3 align-top">
      <div className="flex items-center gap-2.5">
        <Avatar nome={v.nome} />
        <div>
          <div className="text-sm font-bold text-slate-800">{v.nome}</div>
          <div className="text-[11px] text-slate-500">{v.clientes_30d} clientes em 30 dias</div>
        </div>
      </div>
    </td>
    {PERIODOS.map((p) => (
      <td key={p.key} className="px-3 py-3 align-top whitespace-nowrap">
        <div className="text-lg font-black text-slate-800 leading-none">
          {v[p.key].pedidos} <span className="text-xs font-semibold text-slate-400">ped.</span>
        </div>
        <div className="text-xs font-semibold text-slate-600 mt-1">{brl(v[p.key].centavos)}</div>
        <div className="mt-1"><Variacao valor={v[p.key].variacao} rotulo={p.comparacao} /></div>
      </td>
    ))}
    <td className="px-3 py-3 align-top whitespace-nowrap">
      <div className="text-sm font-bold text-slate-800">{brl(v.ticket_medio_30d_centavos)}</div>
      <Participacao pct={v.participacao_30d} />
    </td>
    <td className="px-3 py-3 align-top"><VendidoPorDia serie={v.serie_30d} largura={160} /></td>
    <td className="px-3 py-3 align-top text-[11px] text-slate-600 leading-5 whitespace-nowrap">
      <div><b className="text-slate-800">{v.orcamentos_30d.quantidade}</b> orçamentos · {brlCurto(v.orcamentos_30d.centavos)}</div>
      <div><b className="text-slate-800">{v.carteira_ativa}</b> contas na carteira</div>
      <div><b className={v.canceladas_30d ? 'text-red-600' : 'text-slate-800'}>{v.canceladas_30d}</b> canceladas</div>
    </td>
  </tr>
);

const CardVendedor = ({ v }) => (
  <Card className="p-3 ring-1 ring-slate-200 shadow-sm">
    <div className="flex items-center gap-2.5 mb-3">
      <Avatar nome={v.nome} />
      <div className="min-w-0">
        <div className="text-sm font-bold text-slate-800 truncate">{v.nome}</div>
        <div className="text-[11px] text-slate-500">
          Ticket médio {brl(v.ticket_medio_30d_centavos)} · {v.participacao_30d == null ? '--' : `${Number(v.participacao_30d).toLocaleString('pt-BR')}%`} do faturamento
        </div>
      </div>
    </div>
    <div className="grid grid-cols-3 gap-2">
      {PERIODOS.map((p) => (
        <div key={p.key} className="rounded-lg bg-slate-50 ring-1 ring-slate-200 p-2">
          <div className="text-[10px] font-bold uppercase text-slate-500">{p.curto}</div>
          <div className="text-xl font-black text-slate-800 leading-tight">
            {v[p.key].pedidos}<span className="text-[11px] font-semibold text-slate-400"> ped.</span>
          </div>
          <div className="text-[11px] font-semibold text-slate-600">{brlCurto(v[p.key].centavos)}</div>
          <div className="mt-1"><Variacao valor={v[p.key].variacao} /></div>
        </div>
      ))}
    </div>
    <div className="mt-3"><VendidoPorDia serie={v.serie_30d} /></div>
    <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{v.clientes_30d} clientes</span>
      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{v.orcamentos_30d.quantidade} orçamentos</span>
      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{v.carteira_ativa} na carteira</span>
      <span className={`rounded-full px-2 py-0.5 ${v.canceladas_30d ? 'bg-red-50 text-red-700' : 'bg-slate-100 text-slate-600'}`}>{v.canceladas_30d} canceladas</span>
    </div>
  </Card>
);

/**
 * Secao Vendedores da Dashboard V3. `vendedores`: lista do endpoint, `false` quando
 * a carga falhou, `null` antes de chegar.
 */
const DashboardVendedores = ({ vendedores, loading }) => {
  if (loading) {
    return <Skeleton height="132px" borderRadius="lg" />;
  }

  if (vendedores === false) {
    return (
      <Card className="p-4 ring-1 ring-slate-200 shadow-sm text-center text-sm text-slate-500">
        Não foi possível carregar os vendedores. Atualize a página para tentar de novo.
      </Card>
    );
  }

  if (!vendedores?.length) {
    return (
      <Card className="p-4 ring-1 ring-slate-200 shadow-sm text-center text-sm text-slate-500">
        Nenhum vendedor ativo.
      </Card>
    );
  }

  return (
    <>
      <Card className="hidden md:block p-0 ring-1 ring-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-slate-50 text-[10px] font-bold uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">Vendedor</th>
              {PERIODOS.map((p) => <th key={p.key} className="px-3 py-2">{p.titulo}</th>)}
              <th className="px-3 py-2">Ticket médio (30d)</th>
              <th className="px-3 py-2">Vendido por dia (30d)</th>
              <th className="px-3 py-2">Comercial (30d)</th>
            </tr>
          </thead>
          <tbody>
            {vendedores.map((v) => <LinhaVendedor key={v.id} v={v} />)}
          </tbody>
        </table>
      </Card>
      <div className="md:hidden space-y-3">
        {vendedores.map((v) => <CardVendedor key={v.id} v={v} />)}
      </div>
    </>
  );
};

export default DashboardVendedores;
